import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, doc, updateDoc, addDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { User } from '../types';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'createdAt' | 'totalReservations' | 'displayName'>('createdAt');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    displayName: '',
    phoneNumber: '',
    emailNotifications: true,
    smsNotifications: false,
    isActive: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        lastLoginAt: doc.data().lastLoginAt?.toDate(),
      })) as User[];
      setUsers(data);
    } catch (error) {
      console.error('ユーザー一覧の取得エラー:', error);
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        isActive: !user.isActive,
      });
      setSuccess(`ユーザーを${user.isActive ? '停止' : '有効化'}しました`);
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('ユーザーステータスの更新エラー:', error);
      setError('ユーザーステータスの更新に失敗しました');
    }
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleOpenEditDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        displayName: user.displayName,
        phoneNumber: user.phoneNumber || '',
        emailNotifications: user.preferences?.emailNotifications ?? true,
        smsNotifications: user.preferences?.smsNotifications ?? false,
        isActive: user.isActive,
      });
    } else {
      setEditingUser(null);
      setFormData({
        displayName: '',
        phoneNumber: '',
        emailNotifications: true,
        smsNotifications: false,
        isActive: true,
      });
    }
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingUser(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.displayName) {
        setError('ユーザー名は必須です');
        return;
      }

      if (editingUser) {
        // 更新
        await updateDoc(doc(db, 'users', editingUser.id), {
          displayName: formData.displayName,
          phoneNumber: formData.phoneNumber || null,
          preferences: {
            emailNotifications: formData.emailNotifications,
            smsNotifications: formData.smsNotifications,
          },
          isActive: formData.isActive,
        });
        setSuccess('ユーザーを更新しました');
      } else {
        // 新規作成
        const now = Timestamp.now();
        await addDoc(collection(db, 'users'), {
          displayName: formData.displayName,
          phoneNumber: formData.phoneNumber || null,
          stats: {
            totalReservations: 0,
            cancelledReservations: 0,
            noShowCount: 0,
          },
          preferences: {
            emailNotifications: formData.emailNotifications,
            smsNotifications: formData.smsNotifications,
          },
          isActive: formData.isActive,
          createdAt: now,
          lastLoginAt: null,
        });
        setSuccess('ユーザーを追加しました');
      }

      handleCloseEditDialog();
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('ユーザーの保存エラー:', error);
      setError('ユーザーの保存に失敗しました');
    }
  };

  const handleOpenDeleteDialog = (user: User) => {
    setDeleteTarget(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'users', deleteTarget.id));
      setSuccess('ユーザーを削除しました');
      handleCloseDeleteDialog();
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('ユーザーの削除エラー:', error);
      setError('ユーザーの削除に失敗しました');
    }
  };

  // Filter and sort users
  const filteredAndSortedUsers = React.useMemo(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.displayName.toLowerCase().includes(searchLower) ||
        (user.phoneNumber && user.phoneNumber.includes(searchTerm))
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'active') {
        filtered = filtered.filter(u => u.isActive);
      } else if (filterStatus === 'inactive') {
        filtered = filtered.filter(u => !u.isActive);
      }
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'displayName') {
        return a.displayName.localeCompare(b.displayName, 'ja');
      } else if (sortBy === 'totalReservations') {
        return (b.stats.totalReservations || 0) - (a.stats.totalReservations || 0);
      } else {
        // createdAt
        const aTime = a.createdAt ? a.createdAt.getTime() : 0;
        const bTime = b.createdAt ? b.createdAt.getTime() : 0;
        return bTime - aTime;
      }
    });

    return sorted;
  }, [users, searchTerm, filterStatus, sortBy]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          ユーザー管理
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" color="text.secondary">
            全{users.length}ユーザー / 表示{filteredAndSortedUsers.length}ユーザー
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEditDialog()}
          >
            ユーザーを追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="名前、電話番号で検索"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={filterStatus}
              label="ステータス"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">すべて</MenuItem>
              <MenuItem value="active">有効</MenuItem>
              <MenuItem value="inactive">停止</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>並び替え</InputLabel>
            <Select
              value={sortBy}
              label="並び替え"
              onChange={(e) => setSortBy(e.target.value as 'createdAt' | 'totalReservations' | 'displayName')}
            >
              <MenuItem value="createdAt">登録日順</MenuItem>
              <MenuItem value="displayName">名前順</MenuItem>
              <MenuItem value="totalReservations">予約数順</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ユーザー名</TableCell>
              <TableCell>電話番号</TableCell>
              <TableCell>予約数</TableCell>
              <TableCell>キャンセル数</TableCell>
              <TableCell>無断キャンセル</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  読み込み中...
                </TableCell>
              </TableRow>
            ) : filteredAndSortedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {users.length === 0 ? 'ユーザーがありません' : '条件に一致するユーザーがありません'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {user.displayName}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.phoneNumber || '-'}</TableCell>
                  <TableCell>{user.stats?.totalReservations || 0}</TableCell>
                  <TableCell>{user.stats?.cancelledReservations || 0}</TableCell>
                  <TableCell>
                    {(user.stats?.noShowCount || 0) > 0 ? (
                      <Chip
                        label={user.stats?.noShowCount || 0}
                        color="error"
                        size="small"
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? '有効' : '停止'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(user)}
                      color="primary"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(user)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleStatus(user)}
                      color={user.isActive ? 'error' : 'success'}
                    >
                      {user.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(user)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add User Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'ユーザーを編集' : 'ユーザーを追加'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="ユーザー名"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="電話番号"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              fullWidth
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>通知設定</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.emailNotifications}
                  onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                />
              }
              label="メール通知を有効にする"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.smsNotifications}
                  onChange={(e) => setFormData({ ...formData, smsNotifications: e.target.checked })}
                />
              }
              label="SMS通知を有効にする"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="アカウントを有効にする"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>ユーザーを削除</DialogTitle>
        <DialogContent>
          <Typography>
            ユーザー「{deleteTarget?.displayName}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            ※ この操作は取り消せません。関連する予約データも影響を受ける可能性があります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>ユーザー詳細</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>基本情報</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">ユーザー名</Typography>
                <Typography variant="body1" fontWeight="bold" gutterBottom>{selectedUser.displayName}</Typography>

                {selectedUser.phoneNumber && (
                  <>
                    <Typography variant="body2" color="text.secondary">電話番号</Typography>
                    <Typography variant="body1" gutterBottom>{selectedUser.phoneNumber}</Typography>
                  </>
                )}

                <Typography variant="body2" color="text.secondary">ステータス</Typography>
                <Chip
                  label={selectedUser.isActive ? '有効' : '停止'}
                  color={selectedUser.isActive ? 'success' : 'default'}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Box>

              <Typography variant="h6" gutterBottom>予約統計</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">総予約数</Typography>
                <Typography variant="body1" gutterBottom>{selectedUser.stats.totalReservations || 0}</Typography>

                <Typography variant="body2" color="text.secondary">キャンセル数</Typography>
                <Typography variant="body1" gutterBottom>{selectedUser.stats.cancelledReservations || 0}</Typography>

                <Typography variant="body2" color="text.secondary">無断キャンセル数</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.stats.noShowCount || 0}
                  {selectedUser.stats.noShowCount > 0 && (
                    <Chip
                      label="要注意"
                      color="error"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>

                {selectedUser.stats.totalReservations > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary">キャンセル率</Typography>
                    <Typography variant="body1" gutterBottom>
                      {((selectedUser.stats.cancelledReservations / selectedUser.stats.totalReservations) * 100).toFixed(1)}%
                    </Typography>
                  </>
                )}
              </Box>

              <Typography variant="h6" gutterBottom>通知設定</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">メール通知</Typography>
                <Chip
                  label={selectedUser.preferences.emailNotifications ? '有効' : '無効'}
                  color={selectedUser.preferences.emailNotifications ? 'success' : 'default'}
                  size="small"
                  sx={{ mb: 1 }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>SMS通知</Typography>
                <Chip
                  label={selectedUser.preferences.smsNotifications ? '有効' : '無効'}
                  color={selectedUser.preferences.smsNotifications ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              <Typography variant="h6" gutterBottom>アカウント情報</Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">登録日</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.createdAt ? selectedUser.createdAt.toLocaleDateString('ja-JP') : '-'}
                </Typography>

                {selectedUser.lastLoginAt && (
                  <>
                    <Typography variant="body2" color="text.secondary">最終ログイン</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedUser.lastLoginAt.toLocaleDateString('ja-JP')} {selectedUser.lastLoginAt.toLocaleTimeString('ja-JP')}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Users;
